import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  TrendingUp, BarChart2, Clock, Activity, FileSpreadsheet, RefreshCw,
  Filter, ChevronDown, ArrowUp, ArrowDown,
} from 'lucide-react';

const metrics = [
  { label: 'Total Claims Processed', value: '24,580', change: '+23.4%', positive: true, icon: BarChart2, bg: 'bg-blue-100', color: 'text-blue-600' },
  { label: 'Qualification Rate', value: '16.8%', change: '+2.1%', positive: true, icon: TrendingUp, bg: 'bg-green-100', color: 'text-green-600' },
  { label: 'Avg Processing Time', value: '3.2 min', change: '-15%', positive: true, icon: Clock, bg: 'bg-purple-100', color: 'text-purple-600' },
  { label: 'Monthly Growth', value: '23.4%', change: '+5.2%', positive: true, icon: Activity, bg: 'bg-cyan-100', color: 'text-cyan-600' },
];

const timeSeriesData = [
  { period: 'Jan', processed: 1850, qualified: 310 },
  { period: 'Feb', processed: 2100, qualified: 353 },
  { period: 'Mar', processed: 2300, qualified: 386 },
  { period: 'Apr', processed: 2650, qualified: 445 },
  { period: 'May', processed: 2890, qualified: 485 },
  { period: 'Jun', processed: 3100, qualified: 520 },
  { period: 'Jul', processed: 3350, qualified: 563 },
  { period: 'Aug', processed: 3200, qualified: 538 },
];

const geographicData = [
  { state: 'Texas', processed: 8945, rate: 18.2, revenue: 894500, growth: 28.5 },
  { state: 'California', processed: 7234, rate: 15.7, revenue: 723400, growth: 22.1 },
  { state: 'Florida', processed: 4567, rate: 17.9, revenue: 456700, growth: 19.3 },
  { state: 'New York', processed: 3789, rate: 14.2, revenue: 378900, growth: 15.8 },
  { state: 'Illinois', processed: 2890, rate: 16.4, revenue: 289000, growth: 31.2 },
];

const claimTypeBreakdown = [
  { type: 'RealPage', count: 18450, rate: 17.2, color: 'bg-blue-500' },
  { type: 'PFAS', count: 3890, rate: 14.8, color: 'bg-green-500' },
  { type: 'AFFF', count: 1540, rate: 16.1, color: 'bg-purple-500' },
  { type: 'General', count: 700, rate: 11.5, color: 'bg-gray-500' },
];

const maxProcessed = Math.max(...timeSeriesData.map((d) => d.processed));

const SuccessAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('8_months');
  const [showFilters, setShowFilters] = useState(false);

  return (
    <ContentWrapper>
      <PageHeader
        title="Success Analytics"
        subtitle="Comprehensive analytics for your claims discovery performance"
        breadcrumbs={[
          { label: 'Claims Discovery' },
          { label: 'Success Analytics', isActive: true },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" /> Filters
            </Button>
            <Button variant="outline">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh Data
            </Button>
          </div>
        }
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {metrics.map((m, i) => {
          const Icon = m.icon;
          return (
            <Card key={i} className="bg-white border border-gray-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${m.bg}`}>
                    <Icon className={`h-6 w-6 ${m.color}`} />
                  </div>
                  <Badge variant="success" className="text-xs flex items-center gap-1">
                    {m.positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    {m.change}
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-gray-900">{m.value}</p>
                <p className="text-sm text-gray-500 mt-1">{m.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Processing Volume Chart (bar visualization) */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-gray-900">Processing Volume</CardTitle>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1"
              >
                <option value="3_months">Last 3 Months</option>
                <option value="6_months">Last 6 Months</option>
                <option value="8_months">Last 8 Months</option>
                <option value="1_year">Last Year</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {timeSeriesData.map((d) => (
                <div key={d.period}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600 w-10">{d.period}</span>
                    <span className="text-gray-500 text-xs">{d.processed.toLocaleString()} processed · {d.qualified} qualified</span>
                  </div>
                  <div className="flex gap-1 h-6">
                    <div
                      className="bg-blue-500 rounded-l transition-all"
                      style={{ width: `${((d.processed - d.qualified) / maxProcessed) * 100}%` }}
                      title={`${d.processed - d.qualified} not qualified`}
                    />
                    <div
                      className="bg-green-500 rounded-r transition-all"
                      style={{ width: `${(d.qualified / maxProcessed) * 100}%` }}
                      title={`${d.qualified} qualified`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 bg-blue-500 rounded" /> Processed
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 bg-green-500 rounded" /> Qualified
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Claim Type Breakdown */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-lg text-gray-900">Claims by Type</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {claimTypeBreakdown.map((ct) => {
                const total = claimTypeBreakdown.reduce((sum, x) => sum + x.count, 0);
                const pct = ((ct.count / total) * 100).toFixed(1);
                return (
                  <div key={ct.type}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${ct.color}`} />
                        <span className="text-sm font-medium text-gray-900">{ct.type}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-gray-900">{ct.count.toLocaleString()}</span>
                        <span className="text-xs text-gray-500 ml-2">({pct}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div className={`${ct.color} h-2.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Qualification rate: {ct.rate}%</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geographic Performance */}
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg text-gray-900">Geographic Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">State</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Claims Processed</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Qualification Rate</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Revenue Impact</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-600 uppercase">Growth</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {geographicData.map((geo) => (
                  <tr key={geo.state} className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{geo.state}</td>
                    <td className="p-4 text-gray-700">{geo.processed.toLocaleString()}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-100 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(geo.rate / 20) * 100}%` }} />
                        </div>
                        <span className="text-sm text-gray-700">{geo.rate}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-700">${(geo.revenue / 1000).toFixed(0)}K</td>
                    <td className="p-4">
                      <Badge variant="success" className="text-xs">
                        <ArrowUp className="h-3 w-3 mr-1" />{geo.growth}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </ContentWrapper>
  );
};

export default SuccessAnalytics;